import type { ComponentPropsWithRef, ReactNode } from 'react'

import { Slot } from './Slot'
import { cn, createNonNullableContext } from '@joyful/utils'

export interface RedactionProviderProps {
  children: ReactNode
  isRedactionActive: boolean
}

interface RedactionContextValue {
  isRedactionActive: boolean
}

const RedactionContext =
  createNonNullableContext<RedactionContextValue>('RedactionContext')

export function RedactionProvider({
  children,
  isRedactionActive,
}: RedactionProviderProps) {
  return (
    <RedactionContext.Context value={{ isRedactionActive }}>
      {children}
    </RedactionContext.Context>
  )
}

export interface NoOpRedactionProviderProps {
  children: ReactNode
}

export function NoOpRedactionProvider({
  children,
}: NoOpRedactionProviderProps) {
  return (
    <RedactionProvider isRedactionActive={false}>{children}</RedactionProvider>
  )
}

export function useIsRedactionActive() {
  return RedactionContext.useRequiredContext().isRedactionActive
}

export interface RedactProps extends ComponentPropsWithRef<'span'> {
  /**
   * @default false
   */
  asChild?: boolean

  children?: ReactNode

  /**
   * Optional screenshot-safe replacement. When redaction is on, renders this
   * clean text instead of blurring `children`. When redaction is off, has no
   * effect — `children` still render.
   */
  replacement?: ReactNode
}

export function Redact({
  asChild = false,
  children,
  className,
  replacement,
  ...props
}: RedactProps) {
  const isRedactionActive = useIsRedactionActive()
  const Component = asChild === true ? Slot : 'span'

  if (isRedactionActive === false) {
    return (
      <Component className={className} {...props}>
        {children}
      </Component>
    )
  }

  return (
    <Component
      className={cn(
        'pointer-events-none isolate blur-sm select-none',
        className,
      )}
      aria-hidden="true"
      {...props}
    >
      {replacement ?? children}
    </Component>
  )
}
